import {InternalUserRole} from '@amplify/athena-models';
import Avatar from '@material-ui/core/Avatar';
import Collapse from '@material-ui/core/Collapse';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {Theme, makeStyles} from '@material-ui/core/styles';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import {clsx} from 'clsx';
import {useSession} from 'next-auth/react';
import {KeyboardEvent, MouseEvent, SetStateAction, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {User} from '../Auth/User';
import Admin from '../Icons/Admin';
import AmplifyLogo from '../Icons/AmplifyLogo';
import AmplifyLogoSmall from '../Icons/AmplifyLogoSmall';
import Drill from '../Icons/Drill';
import File from '../Icons/File';
import Hide from '../Icons/Hide';
import Home from '../Icons/Home';
import Key from '../Icons/Key';
import LiveIcon from '../Icons/LiveIcon';
import {Trophy} from 'lucide-react';
import Statistic from '../Icons/Statistic';
import UrlShortener from '../Icons/UrlShortener';
import Users from '../Icons/Users';
import constants from '../constants';
import {Button} from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) => ({
    list: {
        overflowY: 'scroll',
        overflowX: 'hidden',
        maxHeight: 'unset',
        padding: theme.spacing(0, 3, 0, 2),
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        '& $selected': {
            backgroundColor: '#e8edec',
        },
        '@media (max-height: 860px)': {
            maxHeight: 520,
        },
    },
    listClose: {
        padding: '0 11px',
        '& $menuItemText': {
            display: 'none',
        },
        '& $menuItem': {
            display: 'block',
            padding: '8px 15px 2px 7px',
        },
    },
    fullList: {
        width: 'auto',
    },
    menuHeader: {
        padding: theme.spacing(5, 3, 5, 4),
        textAlign: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        '& svg:last-of-type': {
            cursor: 'pointer',
        },
    },
    menuHeaderClose: {
        flexDirection: 'column',
        padding: '15px 11px 10px 12px',
        alignItems: 'flex-start',
        '& svg:last-of-type': {
            transform: 'rotate(180deg)',
            marginTop: theme.spacing(3),
        },
    },
    logo: {
        marginRight: theme.spacing(7),
    },
    nestedList: {
        '& > div': {
            borderRadius: 6,
            '&:hover': {
                backgroundColor: '#3042A4',
            },
        },
        '& $menuItemText': {
            marginLeft: theme.spacing(5),
            '& span': {
                fontWeight: 400,
            },
        },
    },
    nestedListPaper: {
        transform: 'translateX(12px)',
        backgroundColor: '#3042A4',
        fontStyle: 'normal',
        fontWeight: 400,
        borderRadius: '0px 12px 12px 0px',
        fontSize: 18,
        lineHeight: '160%',
        color: '#e8edec',
        '& li': {
            padding: theme.spacing(1.5, 3),
            '&:first-child': {
                pointerEvents: 'none',
                fontWeight: 600,
                opacity: 1,
            },
        },
        '& ul': {
            padding: 0,
        },
        '& $selected': {
            backgroundColor: '#e8edec',
            color: '#456d69',
            '&:hover': {
                color: '#e8edec',
            },
        },
    },
    menuItemText: {
        '& span': {
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: 18,
            lineHeight: '160%',
            color: '#e8edec',
        },
    },
    menuItem: {
        '& div:first-child': {
            minWidth: 'unset',
            justifyContent: 'center',
            marginRight: theme.spacing(3),
        },
        borderRadius: 6,
        '&:last-of-type': {
            '& path': {
                fill: 'none',
                stroke: '#e8edec',
                strokeWidth: 2,
            },
        },
        '&:hover': {
            backgroundColor: '#3042A4',
        },
    },
    selected: {
        '& $menuItemText span': {
            color: '#456d69',
        },
        '& $icon path': {
            fill: '#456d69',
        },
        '&:hover': {
            '& $menuItemText span': {
                color: '#e8edec',
            },
            '& $icon path': {
                fill: '#e8edec',
            },
            '&:last-of-type': {
                '& path': {
                    fill: 'none',
                    stroke: '#e8edec',
                },
            },
            '& $iconUrlShortener path': {
                stroke: '#e8edec',
            },
        },
        '&:last-of-type': {
            '& path': {
                fill: 'none',
                stroke: '#456d69',
            },
        },
        '& $iconUrlShortener path': {
            stroke: '#456d69',
        },
    },
    menuItemFullWidth: {
        width: 'fit-content',
        '& $icon': {
            marginRight: theme.spacing(0.5),
        },
    },
    menuItemHover: {
        backgroundColor: '#3042A4',
        borderRadius: '6px 0px 0px 6px',
    },
    icon: {
        '& path': {
            fill: '#e8edec',
        },
    },
    iconUrlShortener: {
        '& path': {
            stroke: '#e8edec',
        },
    },
    close: {
        fill: '#e8edec',
    },
    avatar: {
        cursor: 'pointer',
        margin: theme.spacing(5, 3, 7, 3),
    },
    avatarClose: {
        margin: theme.spacing(0, 1.3, 7, 1.3),
    },
    avatarBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 40,
        width: 40,
        height: 40,
        fontFamily: 'FredokaRegular',
        fontSize: 24,
        fontWeight: 500,
        color: '#456d69',
        backgroundColor: '#e8edec',
        borderRadius: 20,
        border: '1px solid #e8edec',
        margin: theme.spacing(5, 3, 4, 3),
        '&:hover': {
            color: '#456d69',
            backgroundColor: '#e8edec',
            borderRadius: 20,
            transform: 'scale(1.05)',
            transition: 'transform 0.1s',
        },
    },
    avatarBtnClose: {
        margin: theme.spacing(0, 1.3, 4, 1.3),
    },
    drawer: {
        width: 320,
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    drawerOpen: {
        width: 320,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    paper: {
        background: '#456d69',
        height: '100%',
        overflowY: 'hidden',
        justifyContent: 'space-between',
    },
    drawerClose: {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: 61,
    },
}));

const {BUILD_COMMIT} = constants;
let commitHash = '';
if (BUILD_COMMIT) {
    commitHash = BUILD_COMMIT.substring(0, 7);
}

export default function AppNavMenu({
    state,
    onStateChanged,
    signOut,
}: {
    state: boolean;
    onStateChanged: (state: boolean) => void;
    signOut: () => void;
}) {
    const classes = useStyles();
    const [open, setOpen] = useState(true);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [anchorElClose, setAnchorElClose] = useState<null | HTMLElement>(null);
    const [drawerTransitionActive, setDrawerTransitionActive] = useState(false);
    const onAvatarClicked = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const session = useSession();
    const isAdmin = (session?.data as any)?.roles?.includes(InternalUserRole.AP_MANAGER);

    const handleCollapse = () => setOpen(!open);
    const handleMenu = (event: {currentTarget: SetStateAction<HTMLElement | null>}) => {
        if (!state) {
            if (anchorElClose !== event.currentTarget) {
                setAnchorElClose(event.currentTarget);
            }
        }
    };

    const toggleDrawer = (drawerOpen: boolean) => (event: KeyboardEvent | MouseEvent) => {
        if (
            event.type === 'keydown' &&
            ((event as KeyboardEvent).key === 'Tab' || (event as KeyboardEvent).key === 'Shift')
        ) {
            return;
        }
        setDrawerTransitionActive(true);
        onStateChanged(drawerOpen);
    };

    const history = useHistory();
    const [pathname, setPathname] = useState(history?.location?.pathname);

    const gotoPage = (appid: any) => {
        history.push(`/${appid}`);
        setPathname(`/${appid}`);
    };

    return (
        <div>
            <Drawer
                anchor="left"
                open={state}
                onClose={toggleDrawer(false)}
                variant="permanent"
                className={clsx(classes.drawer, {
                    [classes.drawerOpen]: state,
                    [classes.drawerClose]: !state,
                })}
                classes={{
                    paper: clsx(classes.paper, {
                        [classes.drawerOpen]: state,
                        [classes.drawerClose]: !state,
                    }),
                }}
                PaperProps={{
                    style: drawerTransitionActive ? {overflowX: 'hidden'} : undefined,
                    onTransitionEnd: () => setDrawerTransitionActive(false),
                }}
            >
                <div role="presentation">
                    <div
                        className={clsx(classes.menuHeader, {
                            [classes.menuHeaderClose]: !state,
                        })}
                    >
                        {state && (
                            <>
                                <AmplifyLogo className={classes.logo} />
                                <Hide onClick={toggleDrawer(false)} className={classes.icon} />
                            </>
                        )}
                        {!state && (
                            <>
                                <AmplifyLogoSmall />
                                <Hide onClick={toggleDrawer(true)} className={classes.icon} />
                            </>
                        )}
                    </div>
                    <List
                        className={clsx(classes.list, {
                            [classes.listClose]: !state,
                        })}
                    >
                        <ListItem
                            button
                            key="home"
                            onClick={() => gotoPage('')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/'}
                        >
                            <ListItemIcon>
                                <Home className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Home"
                                className={classes.menuItemText}
                                style={{marginLeft: '-2px'}}
                            />
                        </ListItem>
                        <ListItem
                            button
                            key="clients"
                            onClick={() => gotoPage('agent')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/agent'}
                        >
                            <ListItemIcon>
                                <Users className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Clients"
                                className={classes.menuItemText}
                                style={{marginLeft: '-3px'}}
                            />
                        </ListItem>
                        <ListItem
                            button
                            key="applications"
                            onClick={() => gotoPage('case-manager')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/case-manager'}
                        >
                            <ListItemIcon style={{paddingLeft: '3px'}}>
                                <File className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Applications" className={classes.menuItemText} />
                        </ListItem>
                        <ListItem
                            button
                            key="analytics"
                            onClick={handleCollapse}
                            onMouseOver={handleMenu}
                            className={clsx(classes.menuItem, {
                                [classes.menuItemFullWidth]: !state,
                                [classes.menuItemHover]: !state && Boolean(anchorElClose),
                            })}
                        >
                            <ListItemIcon style={{paddingLeft: '2px'}}>
                                <Statistic className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Analytics" className={classes.menuItemText} />
                            {state &&
                                (open ? (
                                    <ExpandLess className={classes.close} />
                                ) : (
                                    <ExpandMore className={classes.close} />
                                ))}
                        </ListItem>
                        {state ? (
                            <Collapse in={open} timeout="auto" unmountOnExit>
                                <List disablePadding className={classes.nestedList}>
                                    <ListItem
                                        button
                                        key="walkthrough-analytics"
                                        onClick={() => gotoPage('analytics')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/analytics'}
                                    >
                                        <ListItemText
                                            primary="Walkthrough Analytics"
                                            className={classes.menuItemText}
                                        />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="widget-analytics"
                                        onClick={() => gotoPage('widget-analytics')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/widget-analytics'}
                                    >
                                        <ListItemText primary="Widget Analytics" className={classes.menuItemText} />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="advisor-ii-vul"
                                        onClick={() => gotoPage('advisor-ii-vul')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/advisor-ii-vul'}
                                    >
                                        <ListItemText primary="Advisor II VUL" className={classes.menuItemText} />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="client-data-analytics"
                                        onClick={() => gotoPage('client-data-analytics')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/client-data-analytics'}
                                    >
                                        <ListItemText primary="Client Data Report" className={classes.menuItemText} />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="daily-form-submission"
                                        onClick={() => gotoPage('daily-form-submission')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/daily-form-submission'}
                                    >
                                        <ListItemText
                                            primary="Daily Form Submission"
                                            className={classes.menuItemText}
                                        />
                                    </ListItem>
                                    <ListItem
                                        button
                                        key="data-feeds"
                                        onClick={() => gotoPage('data-feeds')}
                                        classes={{selected: classes.selected}}
                                        selected={pathname === '/data-feeds'}
                                    >
                                        <ListItemText primary="Data Feeds" className={classes.menuItemText} />
                                    </ListItem>
                                </List>
                            </Collapse>
                        ) : (
                            <Menu
                                anchorEl={anchorElClose}
                                open={Boolean(anchorElClose)}
                                onClose={() => setAnchorElClose(null)}
                                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                                elevation={0}
                                getContentAnchorEl={null}
                                classes={{paper: classes.nestedListPaper}}
                                MenuListProps={{onMouseLeave: () => setAnchorElClose(null)}}
                            >
                                <MenuItem key="analytics" disabled>
                                    Analytics
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="walkthrough-analytics"
                                    onClick={() => gotoPage('analytics')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/analytics'}
                                >
                                    Walkthrough Analytics
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="widget-analytics"
                                    onClick={() => gotoPage('widget-analytics')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/widget-analytics'}
                                >
                                    Widget Analytics
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="advisor-ii-vul"
                                    onClick={() => gotoPage('advisor-ii-vul')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/advisor-ii-vul'}
                                >
                                    Advisor II VUL
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="client-data-analytics"
                                    onClick={() => gotoPage('client-data-analytics')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/client-data-analytics'}
                                >
                                    Client Data Report
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="daily-form-submission"
                                    onClick={() => gotoPage('daily-form-submission')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/daily-form-submission'}
                                >
                                    Daily Form Submission
                                </MenuItem>
                                <MenuItem
                                    button
                                    key="data-feeds"
                                    onClick={() => gotoPage('data-feeds')}
                                    classes={{selected: classes.selected}}
                                    selected={pathname === '/data-feeds'}
                                >
                                    Data Feeds
                                </MenuItem>
                            </Menu>
                        )}
                        <ListItem
                            button
                            key="tools"
                            onClick={() => gotoPage('tools')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/tools'}
                        >
                            <ListItemIcon style={{paddingLeft: '2px'}}>
                                <Drill className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText
                                primary="External Tools"
                                className={classes.menuItemText}
                                style={{marginLeft: '-3px'}}
                            />
                        </ListItem>
                        <ListItem
                            button
                            key="settings"
                            onClick={() => gotoPage('settings')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/settings'}
                        >
                            <ListItemIcon style={{paddingLeft: '2px'}}>
                                <Key className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText primary="Settings" className={classes.menuItemText} />
                        </ListItem>
                        {isAdmin && (
                            <ListItem
                                button
                                key="userAdmin"
                                onClick={() => gotoPage('user-admin')}
                                className={classes.menuItem}
                                classes={{selected: classes.selected}}
                                selected={pathname === '/user-admin'}
                            >
                                <ListItemIcon>
                                    <Admin className={classes.icon} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="User Admin"
                                    className={classes.menuItemText}
                                    style={{marginLeft: '-2px'}}
                                />
                            </ListItem>
                        )}
                        <ListItem
                            button
                            key="urlShortener"
                            onClick={() => gotoPage('url-shortener')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/url-shortener'}
                        >
                            <ListItemIcon>
                                <UrlShortener className={classes.iconUrlShortener} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Url Shortener"
                                className={classes.menuItemText}
                                style={{marginLeft: '-2px'}}
                            />
                        </ListItem>
                        <ListItem
                            button
                            key="live-call"
                            onClick={() => gotoPage('live-call')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/live-call'}
                        >
                            <ListItemIcon>
                                <LiveIcon className={classes.icon} />
                            </ListItemIcon>
                            <ListItemText
                                primary="Live Call"
                                className={classes.menuItemText}
                                style={{marginLeft: '-3px'}}
                            />
                        </ListItem>
                        <ListItem
                            button
                            key="leaderboard"
                            onClick={() => gotoPage('leaderboard')}
                            className={classes.menuItem}
                            classes={{selected: classes.selected}}
                            selected={pathname === '/leaderboard'}
                        >
                            <ListItemIcon style={{paddingLeft: '2px'}}>
                                <Trophy size={24} className={classes.icon} style={{stroke: '#e8edec', fill: 'none'}} />
                            </ListItemIcon>
                            <ListItemText primary="Leaderboard" className={classes.menuItemText} />
                        </ListItem>
                    </List>
                </div>
                <Button
                    variant="text"
                    disableElevation
                    onClick={onAvatarClicked}
                    className={clsx(classes.avatarBtn, {
                        [classes.avatarBtnClose]: !state,
                    })}
                >
                    {User.Info.name?.[0] ?? ''}
                </Button>
                <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                    getContentAnchorEl={null}
                >
                    <MenuItem>Version: {commitHash}</MenuItem>
                    <MenuItem onClick={signOut}>Logout</MenuItem>
                </Menu>
            </Drawer>
        </div>
    );
}
